import dayjs from 'dayjs';

import { ProjectDetailProps, ProjectRequest, ProjectResponse } from 'types/ProjectTypes';
import { PROJECT_STATUS } from 'utils/constants';

export const getRAGStatus = (project: ProjectResponse | ProjectDetailProps) => {
  const today = dayjs();
  const startDate = dayjs(project?.start_date);
  const finishDate = dayjs(project.finish_date);
  const percentComplete = parseFloat(project.percent_complete) || 0;
  const status = project.status;

  if (finishDate === null || startDate === null) {
    return { rag: 'RED', reason: 'Start or Finish date is not set' };
  }
  // --- RED conditions ---
  if (status === PROJECT_STATUS.onHold.value) {
    // "On Hold" is always Red
    return { rag: 'RED', reason: 'Project is On Hold' };
  }

  if (status === PROJECT_STATUS.rejected.value) {
    // Cancelled
    return { rag: 'RED', reason: 'Project is Cancelled' };
  }

  if (today > finishDate && percentComplete < 100) {
    return { rag: 'RED', reason: 'Project is overdue and incomplete' };
  }

  // --- AMBER conditions ---
  const totalDuration = finishDate.diff(startDate);
  const elapsed = today.diff(startDate);
  const expectedProgress = totalDuration > 0 ? (elapsed / totalDuration) * 100 : 0;

  if (percentComplete === 0 && today > startDate) {
    return { rag: 'AMBER', reason: 'No progress recorded since start date' };
  }

  if (project.estimated_hours === null || project.estimated_cost === null) {
    // Missing estimates is a risk indicator
    if (percentComplete < expectedProgress - 20) {
      return { rag: 'AMBER', reason: 'Behind schedule and missing estimates' };
    }
    return { rag: 'AMBER', reason: 'Missing cost or hour estimates' };
  }

  if (percentComplete < expectedProgress - 20) {
    return { rag: 'AMBER', reason: 'Significantly behind expected progress' };
  }

  // --- GREEN ---
  return { rag: 'GREEN', reason: 'Project is on track' };
};

export const mapProjectResponseToRequest = (data: ProjectDetailProps): ProjectRequest => ({
  project_id: data.id,
  name: data.name,
  description: data.description ?? '',
  status: String(data.status),
  client_contact: data.client_contact?.id ?? '',
  manager: data.manager?.map((m) => m.user_id) ?? [],
  start_date: data.start_date ?? null,
  finish_date: data.finish_date ?? null,
  current_scope: data.current_scope?.id ?? null,
  percent_complete: Number(data.percent_complete) || 0,
  estimated_cost: data.estimated_cost !== null ? Number(data.estimated_cost) : null,
  estimated_hours: data.estimated_hours !== null ? Number(data.estimated_hours) : null,
  current_estimated_cost: data.current_estimated_cost !== null ? Number(data.current_estimated_cost) : null,
  current_estimated_hours: data.current_estimated_hours !== null ? Number(data.current_estimated_hours) : null,
});
